<script>
    import levenshtein from 'js-levenshtein'
    import qs from 'qs'
    import { descend, is, isEmpty, isNil, path, prop, uniq } from 'ramda'

    function b64DecodeUnicode (str) {
        // Going backwards: from bytestream, to percent-encoding, to original string.
        return decodeURIComponent(atob(str).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        }).join(''))
    }

    function normalizeCommentMessage (string) {
        return string.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\x00-\x7F]/g, '').replace(/-/, ' ').replace(/\s\s+/g, ' ')
    }

    let query = qs.parse(location.search.substring(1))

    let timeoutUpdateCommentList
    export let appIsInitialized = false
    export let appTrackMaxScore = 5
    export let appShowTrackList = true
    export let commentFilterTrack = null
    export let commentFilterScore = null
    export let fbAccessToken = query.access_token ?? ''
    export let fbCommentList = []
    export let fbPostId = query.post_id ?? ''

    export let trackListInput = query.tracklist ? b64DecodeUnicode(query.tracklist) : ''

    $: appTrackList = updateTrackList(trackListInput, fbCommentList, appTrackMaxScore || 1)
    $: appCommentList = applyCommentListFilter(fbCommentList, commentFilterTrack, commentFilterScore, appTrackList)
    $: appNoMatchCommentList = updateNoMatchCommentList(fbCommentList, appTrackList)

    $: updateCommentList(fbAccessToken, fbPostId)

    window.fbAsyncInit = function () {
        FB.init({
            appId: '321072322867755',
            autoLogAppEvents: true,
            xfbml: true,
            version: 'v10.0',
        })

        appIsInitialized = true
    }

    function applyCommentListFilter (commentList, commentFilterTrack, commentFilterScore, trackList) {
        if (isNil(commentFilterTrack)) return commentList

        return commentList.filter(function (comment) {
            return trackList[commentFilterTrack].commentList.filter(function (item) {
                return isNil(commentFilterScore) || item.score === commentFilterScore
            }).map(function (item) {
                return item.id
            }).includes(comment.id)
        })
    }

    function setCommentListFilter (event) {
        if (isNil(event.currentTarget.dataset.trackIndex)) return void 0

        event.preventDefault()
        event.stopPropagation()

        if (commentFilterTrack === parseInt(event.currentTarget.dataset.trackIndex) && commentFilterScore === parseInt(event.currentTarget.dataset.scoreIndex)) {
            commentFilterTrack = null
            commentFilterScore = null
        } else {
            commentFilterTrack = parseInt(event.currentTarget.dataset.trackIndex)
            commentFilterScore = parseInt(event.currentTarget.dataset.scoreIndex)
            commentFilterScore = isNaN(commentFilterScore) ? null : commentFilterScore
        }
    }

    function setShowTrackList (event) {
        event.preventDefault()
        event.stopPropagation()

        if (event.currentTarget.classList.contains('selected')) return void 0

        appShowTrackList = !appShowTrackList
    }

    function updateCommentList (access_token, post_id, limit = 5000) {
        function _updateCommentList () {
            if (!access_token || !post_id) return void 0

            FB.api(`/${post_id}/comments`, { access_token, limit }, function (response) {
                if (response && !response.error) {
                    fbCommentList = response.data.map(function (item) {
                        return { ...item, _message: normalizeCommentMessage(item.message) }
                    })
                }
            })
        }

        clearTimeout(timeoutUpdateCommentList)
        timeoutUpdateCommentList = setTimeout(_updateCommentList, 1000)
    }

    function updateNoMatchCommentList (commentList, matchTrackList) {
        if (isEmpty(commentList) || isEmpty(matchTrackList)) return []

        let match = matchTrackList.reduce(function (arr, item) {
            return [...arr, ...uniq(item.commentList.map(prop('id')))]
        }, [])

        return commentList.filter(function (item) {
            return !match.includes(item.id)
        })
    }

    function updateTrackList (trackList, commentList, maxTrackScore) {
        commentFilterTrack = null
        commentFilterScore = null

        return trackList.split('\n').map(function (title) {
            return { title, _title: normalizeCommentMessage(title) }
        }).filter(function (item) {
            return is(String, item._title) && !isEmpty(item._title)
        }).map(function (item) {
            return {
                ...item,
                commentList: commentList.map(function ({ id, _message }) {
                    return { id, score: levenshtein(item._title, _message) }
                }).filter(function ({ score }) {
                    return score < maxTrackScore
                }),
            }
        }).sort(descend(path(['commentList', 'length'])))
    }
</script>

<main>
    <div class="track-list">
        <div class="track-list-control">
            <div class="track-list-control-row">
                <input bind:value={fbAccessToken} class="input-fb-access-token" disabled={!appIsInitialized} placeholder="access token" type="password"/>

                <input bind:value={fbPostId} class="input-fb-post-id" disabled={!appIsInitialized || !fbAccessToken} placeholder="post id"/>

                <input bind:value={appTrackMaxScore} class="input-app-track-max-score" max="15" min="1" placeholder="max. skóre" step="1" type="number"/>
            </div>

            <textarea bind:value={trackListInput} class="input-tracklist" disabled={!appIsInitialized || !fbAccessToken || !fbPostId} placeholder="zoznam skladieb …"></textarea>
        </div>

        <div class="track-list-result">
            <div class="track-list-tab-control">
                <div class="track-list-tab {appShowTrackList ? 'selected' : ''}" on:click={setShowTrackList}>
                    Vyhodnotenie
                </div>

                <div class="track-list-tab {!appShowTrackList ? 'selected' : ''}" on:click={setShowTrackList}>
                    Nezapočítané
                </div>
            </div>

            {#if appShowTrackList}
                {#each appTrackList as track, index}
                    <div class="track-list-item {index === commentFilterTrack ? 'selected' : ''}" on:click={setCommentListFilter} data-track-index={index}>
                        <div class="track-title">
                            {track.title}
                        </div>

                        <div class="track-stat">
                            {#each Array(appTrackMaxScore) as _, scoreIndex}
                                <div class="track-metadata {(index === commentFilterTrack && scoreIndex === commentFilterScore) ? 'selected' : ''}" data-score-index={scoreIndex} data-track-index={index} on:click={setCommentListFilter}>
                                    <div class="track-metadata-title">
                                        {scoreIndex}
                                    </div>

                                    <div class="track-metadata-value">
                                        {track.commentList.filter(function (item) {
                                            return item.score === scoreIndex
                                        }).length}
                                    </div>
                                </div>
                            {/each}

                            <div class="track-metadata">
                                <div class="track-metadata-title">
                                    Spolu
                                </div>

                                <div class="track-metadata-value">
                                    {track.commentList.length}
                                </div>
                            </div>
                        </div>
                    </div>
                {/each}
            {:else}
                {#each appNoMatchCommentList as comment}
                    <div class="no-match-comment-item">
                        <div class="comment-message">
                            {comment.message}
                        </div>
                    </div>
                {/each}
            {/if}
        </div>
    </div>

    <div class="comment-list">
        {#each appCommentList as comment}
            <div class="comment-item">
                <div class="comment-title">
                    <div class="comment-from">
                        {comment?.from?.name || 'Neznámy'}
                    </div>

                    <div class="comment-datetime">
                        {comment.created_time}
                    </div>
                </div>

                <div class="comment-content">
                    {comment.message}
                </div>
            </div>
        {/each}
    </div>
</main>

<style>
    main {
        display: flex;
        flex-direction: row;
        height: 100vh;
        max-height: 100vh;
        overflow: hidden;
        width: 100vw;
        max-width: 100vw;
    }

    .comment-list {
        flex: 1;
        padding: 2rem 2rem 2rem 1rem;
        overflow: auto;
    }

    .comment-item {
        padding: 0.5rem;
        position: relative;
        margin-top: calc(0.5rem + 1px);
        margin-bottom: 0.5rem;
    }

    .comment-item:first-child {
        margin-top: 0;
    }

    .comment-item:hover {
        background: rgba(201, 0, 0, 0.1);
    }

    .comment-item::after {
        content: "";
        background: rgba(0, 0, 0, 0.1);
        position: absolute;
        height: 1px;
        bottom: calc(-0.25rem - 1px);
        left: 1.5rem;
        right: 1.5rem;
    }

    .comment-item:last-child::after {
        display: none;
    }

    .comment-title {
        display: flex;
        flex-direction: row;
        font-size: 0.85rem;
        justify-content: space-between;
        padding-bottom: 0.5rem;
    }

    .input-app-track-max-score {
        flex: 1;
        margin-bottom: 1rem;
        margin-left: 1rem;
    }

    .input-fb-access-token {
        flex: 4;
        margin-bottom: 1rem;
        margin-right: 1rem;
    }

    .input-fb-post-id {
        flex: 4;
        margin-bottom: 1rem;
        margin-left: 1rem;
        margin-right: 1rem;
    }

    .input-tracklist {
        flex: 1;
    }

    .no-match-comment-item {
        margin-bottom: 0.5rem;
        margin-top: calc(0.5rem + 1px);
        padding: 0.5rem;
        position: relative;
    }

    .no-match-comment-item:hover {
        background-color: rgba(201, 0, 0, 0.1);
    }

    .no-match-comment-item:not(:last-child)::after {
        content: "";
        background-color: #cccccc;
        bottom: calc(-0.25rem - 1px);
        height: 1px;
        left: 0.5rem;
        position: absolute;
        right: 0.5rem;
    }

    .track-list {
        display: flex;
        flex: 2;
        flex-direction: column;
        padding: 2rem 1rem 2rem 2rem;
    }

    .track-list-control {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        resize: none;
    }

    .track-list-control-row {
        display: flex;
        flex-direction: row;
    }

    .track-list-result {
        flex: 2;
        margin-top: 2rem;
        overflow: auto;
    }

    .track-list-tab-control {
        border-bottom: 1px solid #cccccc;
        display: flex;
        flex-direction: row;
    }

    .track-list-tab {
        color: #cccccc;
        cursor: pointer;
        flex: 1;
        font-weight: bold;
        padding: 1rem;
        text-align: center;
        text-transform: uppercase;
    }

    .track-list-tab.selected {
        color: #000000;
        cursor: not-allowed;
    }

    .track-list-tab:not(.selected):hover {
        background-color: rgba(201, 0, 0, 0.1);
        color: #000000;
    }

    .track-list-item {
        cursor: pointer;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: 0.5rem;
        margin-top: calc(0.5rem + 1px);
        padding: 1rem;
        position: relative;
    }

    .track-list-item:first-child {
        margin-top: 0;
    }

    .track-list-item.selected,
    .track-list-item:hover {
        background: rgba(201, 0, 0, 0.1);
    }

    .track-list-item::after {
        content: "";
        background: rgba(0, 0, 0, 0.1);
        bottom: calc(-0.25rem - 1px);
        height: 1px;
        left: 1.5rem;
        position: absolute;
        right: 1.5rem;
    }

    .track-list-item:last-child::after {
        display: none;
    }

    .track-title {
        font-weight: bold;
    }

    .track-stat {
        display: flex;
        flex-direction: row;
    }

    .track-metadata {
        align-items: center;
        display: flex;
        flex-direction: column;
        font-size: 0.85rem;
        justify-content: space-between;
        padding: 0 0.5rem;
        min-width: 2rem;
    }

    .track-metadata:last-child {
        font-weight: bold;
        font-size: 1rem;
    }

    .track-metadata:not(:last-child).selected,
    .track-metadata:not(:last-child):hover {
        background: rgba(201, 0, 0, 0.1);
    }

    .track-metadata-title {
        font-weight: bold;
        padding-bottom: 0.5rem;
    }
</style>
